import { Injectable } from '@angular/core';
import { matchNotebookResult, NotebookResultKey, NotebookResultTarget } from './notebook-download-state';

@Injectable({
  providedIn: 'root',
})
export class NotebookDownloadTracker {
  private readonly targets = new Map<string, NotebookResultTarget>();
  private readonly resolvedFallbacks = new Set<string>();
  private readonly owners = new WeakMap<object, string | null>();
  private readonly claimedNotifications = new Map<number, string>();

  register(target: NotebookResultTarget): void {
    this.targets.set(target.requestId, target);
  }

  release(requestId: string): void {
    this.targets.delete(requestId);
    this.resolvedFallbacks.delete(requestId);
    this.claimedNotifications.forEach((owner, id) => {
      if (owner === requestId) {
        this.claimedNotifications.delete(id);
      }
    });
  }

  isRegistered(requestId: string): boolean {
    return this.targets.has(requestId);
  }

  ownerOf(result: NotebookResultKey & object): string | null {
    if (this.owners.has(result)) {
      return this.owners.get(result);
    }
    const owner = this.resolveOwner(result);
    this.owners.set(result, owner);
    if (owner && !result.requestId) {
      this.resolvedFallbacks.add(owner);
    }
    return owner;
  }

  claimNotification(notificationId: number, requestId: string): boolean {
    const owner = this.claimedNotifications.get(notificationId);
    if (owner && owner !== requestId) {
      return false;
    }
    this.claimedNotifications.set(notificationId, requestId);
    return true;
  }

  private resolveOwner(result: NotebookResultKey): string | null {
    let fallbackOwner: string | null = null;
    for (const target of Array.from(this.targets.values())) {
      const match = matchNotebookResult(target, result);
      if (match === 'exact') {
        return target.requestId;
      }
      if (match === 'fallback' && !fallbackOwner && !this.resolvedFallbacks.has(target.requestId)) {
        fallbackOwner = target.requestId;
      }
    }
    return fallbackOwner;
  }
}
